import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  GraduationCap,
  Landmark,
  Lock,
  MapPin,
  ShieldCheck,
  UserRound,
  Users,
} from 'lucide-react';
import {
  EMPLOYMENT_STATUS_LABELS,
  NIGERIAN_STATES,
  QUALIFICATIONS,
  formatShortDate,
} from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from '@/components/ui';
import { PhoneInput } from '@/components/PhoneInput';
import { StatusBadge } from '@/components/StatusBadge';
import { KYC_STATUS, MEMBER_STATUS } from '@/lib/status';

interface Profile {
  id: string;
  membershipNumber: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  email: string;
  phone: string;
  alternatePhone: string | null;
  residentialAddress: string | null;
  city: string | null;
  state: string | null;
  lga: string | null;
  chapter: string | null;
  status: string;
  memberSince: string;
  highestQualification: string | null;
  institution: string | null;
  courseOfStudy: string | null;
  graduationYear: number | null;
  employmentStatus: string | null;
  organisation: string | null;
  jobTitle: string | null;
  nextOfKin: {
    fullName: string | null;
    relationship: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
  } | null;
  kycStatus: string;
  identityLocked: boolean;
  bankAccounts: Array<{ id: string }>;
}

const EMPTY = {
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  phone: '',
  alternatePhone: '',
  residentialAddress: '',
  city: '',
  state: '',
  lga: '',
  chapter: '',
  highestQualification: '',
  institution: '',
  courseOfStudy: '',
  graduationYear: '',
  employmentStatus: '',
  organisation: '',
  jobTitle: '',
  nokFullName: '',
  nokRelationship: '',
  nokPhone: '',
  nokEmail: '',
  nokAddress: '',
};

/**
 * The member's own record.
 *
 * This is the single place personal details live. KYC verifies them and holds
 * the documents; it does not keep a second copy. Bank accounts and security
 * have their own pages and are linked, not repeated.
 */
export default function MemberProfile() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: profile, isLoading } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => api.get<Profile>('/members/me'),
  });

  useEffect(() => {
    if (!profile) return;
    setForm({
      firstName: profile.firstName,
      middleName: profile.middleName ?? '',
      lastName: profile.lastName,
      dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.slice(0, 10) : '',
      gender: profile.gender ?? '',
      phone: profile.phone,
      alternatePhone: profile.alternatePhone ?? '',
      residentialAddress: profile.residentialAddress ?? '',
      city: profile.city ?? '',
      state: profile.state ?? '',
      lga: profile.lga ?? '',
      chapter: profile.chapter ?? '',
      highestQualification: profile.highestQualification ?? '',
      institution: profile.institution ?? '',
      courseOfStudy: profile.courseOfStudy ?? '',
      graduationYear: profile.graduationYear ? String(profile.graduationYear) : '',
      employmentStatus: profile.employmentStatus ?? '',
      organisation: profile.organisation ?? '',
      jobTitle: profile.jobTitle ?? '',
      nokFullName: profile.nextOfKin?.fullName ?? '',
      nokRelationship: profile.nextOfKin?.relationship ?? '',
      nokPhone: profile.nextOfKin?.phone ?? '',
      nokEmail: profile.nextOfKin?.email ?? '',
      nokAddress: profile.nextOfKin?.address ?? '',
    });
  }, [profile]);

  const set = (key: keyof typeof EMPTY) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = useMutation({
    mutationFn: () =>
      api.patch('/members/me', {
        firstName: form.firstName,
        middleName: form.middleName || undefined,
        lastName: form.lastName,
        dateOfBirth: form.dateOfBirth || undefined,
        gender: form.gender || undefined,
        phone: form.phone,
        alternatePhone: form.alternatePhone || undefined,
        residentialAddress: form.residentialAddress || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        lga: form.lga || undefined,
        chapter: form.chapter || undefined,
        highestQualification: form.highestQualification || undefined,
        institution: form.institution || undefined,
        courseOfStudy: form.courseOfStudy || undefined,
        graduationYear: form.graduationYear || undefined,
        employmentStatus: form.employmentStatus || undefined,
        organisation: form.organisation || undefined,
        jobTitle: form.jobTitle || undefined,
        ...(form.nokFullName || form.nokPhone
          ? {
              nextOfKin: {
                fullName: form.nokFullName || undefined,
                relationship: form.nokRelationship || undefined,
                phone: form.nokPhone || undefined,
                email: form.nokEmail || undefined,
                address: form.nokAddress || undefined,
              },
            }
          : {}),
      }),
    onSuccess: () => {
      toast.success('Profile updated.');
      setFieldErrors({});
      void queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    },
    onError: (error) => {
      if (error instanceof ApiError) setFieldErrors(error.fieldErrors);
      toast.error(error instanceof Error ? error.message : 'Could not save.');
    },
  });

  if (isLoading || !profile) return <Skeleton className="h-96" />;

  const locked = profile.identityLocked;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow={`Membership ${profile.membershipNumber}`}
        title="Profile"
        description={`Member since ${formatShortDate(new Date(profile.memberSince))}`}
        actions={
          <div className="flex gap-2">
            <StatusBadge map={MEMBER_STATUS} status={profile.status} />
            <StatusBadge map={KYC_STATUS} status={profile.kycStatus} />
          </div>
        }
      />

      {locked && (
        <Alert tone="info" className="mb-6">
          <span className="inline-flex items-start gap-2">
            <Lock size={14} className="mt-0.5 shrink-0" />
            Your name, date of birth and gender are fixed now that your identity is verified —
            changing them would invalidate the document on file. Everything else stays editable.
            Contact UNIFEMGA if a verified detail is wrong.
          </span>
        </Alert>
      )}

      <div className="space-y-6">
        {/* Identity */}
        <Section icon={<UserRound size={16} />} title="Identity" locked={locked}>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="First name" htmlFor="firstName" required error={fieldErrors['firstName']}>
              <Input id="firstName" value={form.firstName} onChange={set('firstName')} disabled={locked} />
            </Field>
            <Field label="Last name" htmlFor="lastName" required error={fieldErrors['lastName']}>
              <Input id="lastName" value={form.lastName} onChange={set('lastName')} disabled={locked} />
            </Field>
          </div>

          <Field label="Middle name" htmlFor="middleName" error={fieldErrors['middleName']}>
            <Input id="middleName" value={form.middleName} onChange={set('middleName')} disabled={locked} />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Date of birth" htmlFor="dob" error={fieldErrors['dateOfBirth']}>
              <Input id="dob" type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} disabled={locked} />
            </Field>
            <Field label="Gender" htmlFor="gender" error={fieldErrors['gender']}>
              <Select id="gender" value={form.gender} onChange={set('gender')} disabled={locked}>
                <option value="">Select…</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              </Select>
            </Field>
          </div>
        </Section>

        {/* Contact */}
        <Section icon={<Building2 size={16} />} title="Contact">
          <Field
            label="Email address"
            htmlFor="email"
            hint="Contact UNIFEMGA to change the address your account is registered under."
          >
            <Input id="email" value={profile.email} disabled />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Phone number" htmlFor="phone" required error={fieldErrors['phone']}>
              <PhoneInput
                id="phone"
                value={form.phone}
                onChange={(v) => setForm((c) => ({ ...c, phone: v }))}
              />
            </Field>
            <Field label="Alternate phone" htmlFor="altPhone" error={fieldErrors['alternatePhone']}>
              <PhoneInput
                id="altPhone"
                value={form.alternatePhone}
                onChange={(v) => setForm((c) => ({ ...c, alternatePhone: v }))}
              />
            </Field>
          </div>
        </Section>

        {/* Address */}
        <Section icon={<MapPin size={16} />} title="Address">
          <Field label="Residential address" htmlFor="address" error={fieldErrors['residentialAddress']}>
            <Input id="address" value={form.residentialAddress} onChange={set('residentialAddress')} />
          </Field>

          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="City / town" htmlFor="city" error={fieldErrors['city']}>
              <Input id="city" value={form.city} onChange={set('city')} />
            </Field>
            <Field label="State" htmlFor="state" error={fieldErrors['state']}>
              <Select id="state" value={form.state} onChange={set('state')}>
                <option value="">Select…</option>
                {NIGERIAN_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="LGA" htmlFor="lga" error={fieldErrors['lga']}>
              <Input id="lga" value={form.lga} onChange={set('lga')} />
            </Field>
          </div>
        </Section>

        {/* Membership */}
        <Section icon={<Users size={16} />} title="Membership">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Membership number" htmlFor="membershipNumber" hint="Issued by UNIFEMGA.">
              <Input id="membershipNumber" value={profile.membershipNumber} disabled className="numeric" />
            </Field>
            <Field
              label="Chapter"
              htmlFor="chapter"
              error={fieldErrors['chapter']}
              hint="The UNIFEMGA chapter you belong to."
            >
              <Input
                id="chapter"
                value={form.chapter}
                onChange={set('chapter')}
                placeholder="e.g. Ile-Ife, Lagos, Abuja"
              />
            </Field>
          </div>
        </Section>

        {/* Education */}
        <Section icon={<GraduationCap size={16} />} title="Education">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Highest qualification"
              htmlFor="qualification"
              error={fieldErrors['highestQualification']}
            >
              <Select
                id="qualification"
                value={form.highestQualification}
                onChange={set('highestQualification')}
              >
                <option value="">Select…</option>
                {QUALIFICATIONS.map((qualification) => (
                  <option key={qualification} value={qualification}>
                    {qualification}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Year of graduation" htmlFor="graduationYear" error={fieldErrors['graduationYear']}>
              <Input
                id="graduationYear"
                type="number"
                className="numeric"
                min={1950}
                max={new Date().getFullYear() + 10}
                value={form.graduationYear}
                onChange={set('graduationYear')}
              />
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Institution" htmlFor="institution" error={fieldErrors['institution']}>
              <Input
                id="institution"
                value={form.institution}
                onChange={set('institution')}
                placeholder="Obafemi Awolowo University"
              />
            </Field>
            <Field label="Course of study" htmlFor="course" error={fieldErrors['courseOfStudy']}>
              <Input id="course" value={form.courseOfStudy} onChange={set('courseOfStudy')} />
            </Field>
          </div>
        </Section>

        {/* Employment */}
        <Section icon={<Building2 size={16} />} title="Employment">
          <Field label="Status" htmlFor="employment" error={fieldErrors['employmentStatus']}>
            <Select id="employment" value={form.employmentStatus} onChange={set('employmentStatus')}>
              <option value="">Select…</option>
              {Object.entries(EMPLOYMENT_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Organisation" htmlFor="organisation" error={fieldErrors['organisation']}>
              <Input id="organisation" value={form.organisation} onChange={set('organisation')} />
            </Field>
            <Field label="Job title" htmlFor="jobTitle" error={fieldErrors['jobTitle']}>
              <Input id="jobTitle" value={form.jobTitle} onChange={set('jobTitle')} />
            </Field>
          </div>
        </Section>

        {/* Next of kin */}
        <Section
          icon={<ShieldCheck size={16} />}
          title="Next of kin"
          description="Someone UNIFEMGA can contact about your membership if we cannot reach you."
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full name" htmlFor="nokName" error={fieldErrors['nextOfKin.fullName']}>
              <Input id="nokName" value={form.nokFullName} onChange={set('nokFullName')} />
            </Field>
            <Field
              label="Relationship"
              htmlFor="nokRel"
              error={fieldErrors['nextOfKin.relationship']}
              hint="e.g. Spouse, brother"
            >
              <Input id="nokRel" value={form.nokRelationship} onChange={set('nokRelationship')} />
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Phone number" htmlFor="nokPhone" error={fieldErrors['nextOfKin.phone']}>
              <PhoneInput
                id="nokPhone"
                value={form.nokPhone}
                onChange={(v) => setForm((c) => ({ ...c, nokPhone: v }))}
              />
            </Field>
            <Field label="Email" htmlFor="nokEmail" error={fieldErrors['nextOfKin.email']}>
              <Input id="nokEmail" type="email" value={form.nokEmail} onChange={set('nokEmail')} />
            </Field>
          </div>

          <Field label="Address" htmlFor="nokAddress" error={fieldErrors['nextOfKin.address']}>
            <Input id="nokAddress" value={form.nokAddress} onChange={set('nokAddress')} />
          </Field>
        </Section>

        {/* One save for the whole record, so a member is never left wondering
            which section they forgot to submit. */}
        <div className="sticky bottom-4 z-10">
          <Card className="flex flex-wrap items-center justify-between gap-4 p-4 shadow-floating">
            <p className="text-sm text-foreground-secondary">
              Changes apply to your whole profile.
            </p>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save changes
            </Button>
          </Card>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <QuickLink
          to="/portal/bank-accounts"
          icon={<Landmark size={18} />}
          title="Bank accounts"
          detail={
            profile.bankAccounts.length === 0
              ? 'None on file'
              : `${profile.bankAccounts.length} account${profile.bankAccounts.length === 1 ? '' : 's'}`
          }
          warn={profile.bankAccounts.length === 0}
        />
        <QuickLink
          to="/portal/kyc"
          icon={<BadgeCheck size={18} />}
          title="Verification"
          detail={KYC_STATUS[profile.kycStatus as keyof typeof KYC_STATUS]?.label ?? 'Not started'}
          warn={profile.kycStatus !== 'APPROVED'}
        />
        <QuickLink
          to="/portal/settings"
          icon={<ShieldCheck size={18} />}
          title="Security"
          detail="Password and two-factor"
        />
      </div>
    </div>
  );
}

function Section({
  icon,
  title,
  description,
  locked,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  locked?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground-secondary">
            {icon}
          </span>
          <div>
            <h2 className="flex items-center gap-2 font-heading font-bold text-foreground">
              {title}
              {locked && (
                <span className="inline-flex items-center gap-1 rounded-full bg-neutral-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-soft-foreground">
                  <Lock size={9} /> Verified
                </span>
              )}
            </h2>
            {description && (
              <p className="mt-0.5 text-sm text-foreground-secondary">{description}</p>
            )}
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function QuickLink({
  to,
  icon,
  title,
  detail,
  warn,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  detail: string;
  warn?: boolean;
}) {
  return (
    <Link to={to}>
      <Card className="surface-interactive h-full p-5">
        <span
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-xl',
            warn ? 'bg-warning-soft text-warning' : 'bg-muted text-foreground-secondary',
          )}
        >
          {icon}
        </span>
        <p className="mt-3 font-heading font-bold text-foreground">{title}</p>
        <p className="mt-0.5 flex items-center gap-1 text-sm text-foreground-secondary">
          {detail} <ArrowRight size={13} />
        </p>
      </Card>
    </Link>
  );
}
